/**
 * HeaderItem.js
 */

import React, {useState, useContext} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {actionIcBuild24px} from '@ey/motif-react-wrapper/assets/icons/index';
import {IconoirMinus, IconoirPlus, IconoirCustomMoreVerticalAlt} from '@ey-xd/motif-icon';
import {documentTypes, clientSideEventNames, serviceURL, entities} from '../../../../util/uiconstants';
import {labels} from '../../../../util/utils';
import env from '../../../../util/env';
import Utility from '../../../../util/utilityfunctions';
import {useClearAllComments} from '../../../../util/customHooks';
import Validation from '../Common/Validation';
import EllipseControl from '../../EllipsesControl/EllipsesControl';
import MotifDropdown from '@ey/motif-react-wrapper/Dropdown';
import DropdownItem from '../../BaseComponentWrappers/MotifDropdownItem';
import SectionList from '../Section/SectionList';
import {DocumentTypeIdContext} from '../../../Main/Forms/CanvasFormContainer';
import CompareSnapshotLabel from '../CompareSnapshotLabel';
import AddEditCustomModal from '../../../Main/Forms/AddEditCustom/AddEditCustomModal';
import ConfirmationModal from '../../Modal/Modal';
import {deleteHeader} from '../../../../actions/FormData/formdataactions';
import GrayFade from '../../GrayFade/GrayFade';
import MotifButton from '@ey/motif-react-wrapper/Button';
import Icon from '../../BaseComponentWrappers/MotifIcon';

export default function HeaderItem(props) {
	const {header, formData, documentId, engagementId, isSnapshotView, changesView} = props;

	const [headerExpanded, setHeaderExpanded] = useState(false),
		[selectedSection, setSelectedSection] = useState(undefined),
		[isOpen, setIsOpen] = useState(false),
		[showEditModal, setShowEditModal] = useState(false),
		[showDeleteModal, setShowDeleteModal] = useState(false),
		[modalType, setModalType] = useState(undefined),
		[currentHeaderId, setCurrentHeaderId] = useState(header.id),
		[isLoading, setIsLoading] = useState(false);

	const documentTypeId = useContext(DocumentTypeIdContext);

	const signOffs = useSelector((store) => store.signOffs);
	const dispatch = useDispatch();

	const {setClearAllComments} = useClearAllComments(
		engagementId,
		documentId,
		documentTypeId,
		entities.FormHeader,
		header?.id,
		() => {
			setIsLoading(false);
			setClearAllComments(false);
		}
	);

	const expandCollapse = (e) => {
		const currentTarget = e.currentTarget;
		setHeaderExpanded(!headerExpanded);
	};

	const setClickOutside = () => {
		setIsOpen(false);
	};

	const handleToggle = (e) => {
		setIsOpen(!isOpen);
		e.stopPropagation();
	};

	const cancel = () => {
		if (modalType === labels.delete) {
			setShowDeleteModal(false);
		} else {
			setShowEditModal(false);
		}
	};

	const confirmActionClick = () => {
		dispatch(
			deleteHeader(env.getBaseURL(serviceURL, engagementId) + `forms/${documentId}/headers/${currentHeaderId}`, {
				documentId: documentId,
				headerId: currentHeaderId
			})
		)
			.then(() => {
				window.dispatchEvent(
					new CustomEvent(clientSideEventNames.onCreateOrEdit, {
						detail: {signOffs: signOffs}
					})
				);
				setCurrentHeaderId(0);
			})
			.catch(() => {
				setShowDeleteModal(false);
			});
	};

	return (
		<GrayFade isGrayFade={isLoading}>
			<Wrapper className="SectionBodyAccordion">
				<section className={`accordionWrapper ${headerExpanded ? 'selected-accordion' : ''}`}>
					<section className="SelectedCanvasFormHeader">
						<section className="selectedCanvasFormHeaderWrapper" id={currentHeaderId} onClick={expandCollapse}>
							<section className="plusminusIcons" title={!headerExpanded ? labels.expand : labels.collapse}>
								<Icon
									className={`medium`}
									src={!headerExpanded ? IconoirPlus : IconoirMinus}
									title={!headerExpanded ? labels.expand : labels.collapse}
								/>
							</section>
							<h2 className="FormTitleHeader">
								<EllipseControl
									content={Utility.getHtmlTextElement(header.name)}
									id={currentHeaderId}
									tooltip={Utility.getHtmlTextElement(header.name)}
									displayId={''}
									noOfLines={1}
								/>
							</h2>
						</section>
						<section className="RightSideHeader">
							{!isSnapshotView ? (
								<Validation
									documentId={documentId}
									headerId={currentHeaderId}
									displayFor={'header'}
									clearComments={() => {
										setIsLoading(true);
										setClearAllComments(true);
									}}
								/>
							) : (
								<CompareSnapshotLabel snapshotObject={header} />
							)}
							{header.isCustom && (
								<section className="custom-labels">
									<span className={`edit-icon ${header.isCustom ? 'showIcon' : 'hiddenIcon'}`} title={labels.custom}>
										<Icon src={actionIcBuild24px} title={labels.custom} />
									</span>
									{!isSnapshotView && documentTypeId !== documentTypes.INDEPENDENCEFORMINDIVIDUAL && header.isCustom && (
										<section className="headerOptions">
											<MotifDropdown
												open={isOpen}
												id={'sectionOption'}
												aria-labelledby={'dropdown-trigger'}
												handleClickOutside={setClickOutside}
												usePortal
												trigger={
													<MotifButton
														id={'dropdown-trigger'}
														variant="text"
														onClick={handleToggle}
														ariaControls={'sectionOption'}
														ariaExpanded={isOpen}
														ariaLabel="dropdown trigger"
													>
														<Icon src={IconoirCustomMoreVerticalAlt} title={labels.custom} />
													</MotifButton>
												}
												children={
													<>
														<DropdownItem
															onClick={() => {
																setModalType(labels.edit);
																setShowEditModal(!showEditModal);
															}}
															children={labels.edit}
														/>
														<DropdownItem
															onClick={() => {
																setModalType(labels.delete);
																setShowDeleteModal(!showDeleteModal);
															}}
															children={labels.delete}
														/>
													</>
												}
											/>
										</section>
									)}
								</section>
							)}
						</section>
					</section>
				</section>
				{headerExpanded && (
					<SectionList
						formData={formData}
						isSnapshotView={isSnapshotView}
						sections={header.sections?.filter((s) => s.isDisplayed === true || s.isCustom === true)}
						header={header}
						selectedSectionId={selectedSection?.id}
						setSelectedSection={setSelectedSection}
						engagementId={engagementId}
						documentId={documentId}
						changesView={changesView}
					/>
				)}
				{showEditModal && (
					<>
						<AddEditCustomModal
							afterSave={() => {
								setShowEditModal(false);
								window.dispatchEvent(
									new CustomEvent(clientSideEventNames.onCreateOrEdit, {
										detail: {selectedId: currentHeaderId, signOffs: signOffs}
									})
								);
								window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
							}}
							formData={formData}
							entityParentId={currentHeaderId}
							documentId={documentId}
							engagementId={engagementId}
							entity={'header'}
							entityId={currentHeaderId}
							entityValue={header.name}
							isEdit
							isCustom
							onHide={cancel}
							showTabs={false}
						/>
					</>
				)}

				{showDeleteModal && (
					<ConfirmationModal
						show={showDeleteModal}
						showButtons
						title={labels.deleteHeader}
						confirmBtnTitle={labels.delete}
						cancelBtnTitle={labels.cancelLabel}
						onOkClick={confirmActionClick}
						onHide={cancel}
						closeTitle={labels.closeLabel}
						modalsize="small"
					>
						<span>{labels.deleteHeaderTitle}</span>
						<span>{header?.name}</span>
					</ConfirmationModal>
				)}
			</Wrapper>
		</GrayFade>
	);
}

const Wrapper = styled.section`
	display: block;
	width: calc(100% - var(--px-3));
	margin-bottom: var(--px-5);
	&:last-child {
		margin-bottom: var(--px-10);
	}
	.FormBodyLoader {
		display: flex;
		width: 100%;
		justify-content: center;
	}
	.accordionWrapper {
		display: block;
		width: 100%;
		padding: var(--px-10) var(--px-20) var(--px-10) var(--px-10);
		.SelectedCanvasFormHeader {
			.FormTitleHeader {
				.diffins {
					background-color: #e6ffe6;
					font-family: inherit;
					text-decoration: none;
				}
				.diffdel {
					color: #999;
					background-color: #ffe6e6;
					font-family: inherit;
				}
				.mod {
					background-color: #ffe1ac;
					font-family: inherit;
				}
			}
		}
		.section-container {
			display: flex;
			width: 100%;
		}
		.section-Wrapper {
			display: flex;
			width: calc(100% - var(--px-160));
			cursor: pointer;
		}
		.plusminusIcons {
			display: flex;
			align-items: center;
			transition: 0.5s;
			transition-property: height;
			.motif-icon {
				display: flex;
				align-items: center;
			}
			svg {
				color: var(--neutrals-900);
			}
		}
		.sectionName {
			display: flex;
			width: 100%;
			max-width: 95vw;
			@media only screen and (max-width: 1280px) {
				max-width: 76vw;
			}
			align-items: center;
			font-size: var(--px-14);
			line-height: var(--px-15);
			margin-left: var(--px-10);
		}
		.sectionOptions {
			display: inline-flex;
			justify-content: flex-end;
			width: auto;
			min-width: var(--px-60);
			max-width: var(--px-150);
			margin-left: auto;
			svg {
				width: var(--px-20) !important;
				height: var(--px-20) !important;
				padding: 0;
			}
			.icon-wrapper {
				display: flex;
				margin: 0;
				align-items: center;
				.section-edit-icon {
					margin-right: var(--px-10);
					height: var(--px-24);
					svg {
						padding: 0;
						cursor: default;
					}
				}
				.hiddenIcon {
					display: none;
				}
				.showIcon {
					display: inline-block;
				}
				.section-options {
					.motif-dropdown {
						display: flex;
						position: relative;
						.motif-button {
							display: flex;
						}
					}
				}
			}
		}
	}
	.section-body {
		display: flex;
		justify-content: center;
		font-size: var(--px-14);
		width: 100%;
		background-color: var(--neutrals-00white);
		border-bottom: var(--px-1) solid var(--neutrals-200);
		padding: var(--px-10) var(--px-10);
		.section-edit-icon {
			display: flex;
			height: var(--px-24);
			width: var(--px-24);
			margin: var(--px-15) 0 0 var(--px-10);
			justify-content: center;
			align-items: center;
		}
	}
`;
